import type { ReactNode } from "react";

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import type { DataTableInstance } from "../ui-kit/tables/data-table/table-features";

import { UserReferenceResponse } from "../../api";
import { MonitoringKind, MonitoringUserInfo } from "./monitoring-assignments";
import { MonitoringUserBubble } from "./monitoring-user-bubbles";

interface FilterablePhysician {
  uuid?: string;
  assigned_monitoring_users?: UserReferenceResponse[] | null;
}

interface MonitoringUserFilterConfig<T> {
  /** Resolves one row to the physicians it covers. */
  physiciansOf: (item: T) => FilterablePhysician[];
  /** Resolves one physician to its assigned monitoring users. */
  resolve: (physician: FilterablePhysician) => MonitoringUserInfo[];
  /** Kind for a monitoring-user uuid, or undefined when unknown. */
  kindOf: (uuid: string) => MonitoringKind | undefined;
}

/**
 * "Care team" multi-select filter over a table's monitoring users.
 *
 * The data source is injected via `config`: `MonitoringAssignments` joins the
 * users in from a separate physicians fetch (the patients list), while
 * `MonitoringUsersDirectory` reads them off the row's embedded
 * `assigned_monitoring_users` (the waiting rooms). See the call sites for the
 * two adapters.
 *
 * `physiciansOf` maps a row to the physicians it covers, so the filter works
 * both on tables of patients (whose rows hold a `physicians` array) and on
 * tables whose rows are themselves physicians (the patient Physicians tab).
 */
export function monitoringUserFilter<T>({
  physiciansOf,
  resolve,
  kindOf,
}: MonitoringUserFilterConfig<T>) {
  const options = (table: DataTableInstance<T>) => {
    const map = new Map<string, string>();
    for (const row of table.getCoreRowModel().rows) {
      for (const physician of physiciansOf(row.original)) {
        for (const user of resolve(physician)) {
          map.set(user.uuid, user.username);
        }
      }
    }
    return new Map(
      [...map.entries()].sort(([, a], [, b]) => a.localeCompare(b)),
    );
  };

  return {
    kind: "multi" as const,
    group: i18n._(msg`Care team`),
    options,
    renderOption: (value: unknown, label: string): ReactNode => {
      const uuid = String(value);
      const kind = kindOf(uuid);
      return (
        <span className="flex items-center gap-2">
          {kind && (
            <MonitoringUserBubble user={{ uuid, username: label, kind }} />
          )}
          {label}
        </span>
      );
    },
    disabled: (table: DataTableInstance<T>) => options(table).size === 0,
    disabledMessage: i18n._(msg`No monitoring users available`),
    predicate: (item: T, selected: unknown[]) =>
      physiciansOf(item).some((physician) =>
        resolve(physician).some((user) => selected.includes(user.uuid)),
      ),
  };
}
